/* eslint-disable no-await-in-loop */
/* eslint-disable no-restricted-syntax */
import React, { Component } from 'react';
import { withStyles } from '@material-ui/core/styles';
import Paper from '@material-ui/core/Paper';
import CircularProgress from '@material-ui/core/CircularProgress';
import { withLDConsumer } from 'launchdarkly-react-client-sdk';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@ublend-npm/aulaui-next';
import MenuItem from '@material-ui/core/MenuItem';
import Button from '@material-ui/core/Button';
import chunk from 'lodash/chunk';
import startCase from 'lodash/startCase';
import UserTable from '../../shared/Table/Table';
import BasicInfoForm from '../../shared/BasicInfoForm/BasicInfoForm';
import getSSO from '../../../utils/getSSO';
import { isValidEmail } from '../../../utils/email';
import { analyticsTrackEvent } from '../../../utils/analytics';
import {
  ADMIN_ROLE_ASSIGNED_TO_USER,
  ADMIN_USER_REMOVED_FROM_SPACE,
} from '../../../constants/analytics';
import { mergeUserCustomFields } from '../../../utils/formatAdminUser';
import {
  getSisUserCustomFieldError,
  SIS_USER_CUSTOM_FIELD_KEYS,
  SIS_USER_CUSTOM_FIELD_MAX_LENGTH,
} from '../../../constants/sisUserCustomFields';

const LEGACY_ROLES = [
  { id: 'student', title: 'Student' },
  { id: 'educator', title: 'Educator' },
];

const styles = (theme) => ({
  loaderContainer: {
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  container: {
    height: 'calc(100% - 60px)',
    paddingTop: 60,
  },
  table: {
    paddingBottom: 60,
  },
  basicInfoForm: {
    padding: '0 16px',
  },
  menuItem: {
    padding: '4px 24px',
    fontSize: 14,
    fontWeight: theme.typography.fontWeightRegular,
    width: 172,
    '&:hover': {
      backgroundColor: theme.palette.common.grey,
    },
  },
});

const BtnTitle = (text, history, { name, externalId, internalId }) => {
  const searchParams = new URLSearchParams({
    addUser: 'true',
    userName: name,
    userId: externalId,
    userInternalId: internalId,
  });

  return (
    <Button
      color="primary"
      onClick={() =>
        history.push({
          search: `?${searchParams.toString()}`,
        })
      }
      style={{ fontSize: '16px', marginLeft: '-8px' }}
    >
      {text}
    </Button>
  );
};

const CHUNK_SIZE = 50;
class User extends Component<any> {
  state = {
    spaces: [],
    saved: false,
    userId: undefined,
    textFields: {
      firstName: '',
      lastName: '',
      externalId: undefined,
      email: '',
      studentId: '',
      degreeCourse: '',
      sso: false,
      userRoles: [],
    },
    loading: undefined,
    selected: undefined,
    custom: undefined,
    removeUser: undefined,
    isRemoving: undefined,
    avatar: undefined,
  };

  componentWillMount = async () => {
    void this.fetchUser(this.props.match.params.userId);
    // Fetch roles if not already loaded
    const { webAppFtForRbac202505 } = this.props.flags || {};
    if (
      webAppFtForRbac202505 &&
      this.props.fetchRoles &&
      (!this.props.roles || this.props.roles.length === 0)
    ) {
      this.props.fetchRoles();
    }
  };

  componentWillReceiveProps = (nextProps) => {
    if (nextProps.match.params.userId !== this.props.match.params.userId) {
      void this.fetchUser(nextProps.match.params.userId);
    }
  };

  handleSelect = (selected) => {
    this.setState({ selected });
  };

  getRoleName = (roleId) => {
    if (!roleId || !this.props.roles) {
      return null;
    }
    const role = this.props.roles.find((r) => (r.id || r._id) === roleId);
    return role ? role.title || role.name || null : null;
  };

  fetchUser = async (id) => {
    this.setState({ loading: true });
    const user = await this.props.fetchUser(id);
    if (JSON.stringify(user) === '{}') {
      this.props.history.push('/');
      return;
    }

    this.setState({
      loading: false,
      userId: user.id,
      textFields: {
        firstName: user.firstName,
        lastName: user.lastName,
        email: user.email,
        externalId: user.externalId,
        studentId: user.custom?.studentId || '',
        degreeCourse: user.custom?.degreeCourse || '',
        sso: getSSO({ user, institution: this.props as any }),
        userRoles: user.userRoles,
      },
      custom: user.custom ? user.custom : {},
      avatar: user.avatar,
      spaces: user.spaces.map((space) => {
        const { webAppFtForRbac202505 } = this.props.flags || {};
        const roleId = space.roleId;
        let roleName;

        if (webAppFtForRbac202505) {
          // When RBAC is enabled, get role name from roleId
          roleName = roleId ? this.getRoleName(roleId) : null;
        } else {
          // When RBAC is disabled, use role string directly
          roleName = space.role === 'student' ? 'Student' : 'Educator';
        }

        return {
          ...space,
          custom: JSON.stringify(space.custom || {}, null, 2),
          roleId,
          roleName,
        };
      }),
    });
  };

  preSaveCheck = async (data) => {
    const { firstName, lastName, email, studentId, degreeCourse } = data;
    const errors: any = {};

    if (!firstName || firstName.trim() === '') {
      errors.firstName = 'Incorrect first name';
    }

    if (!lastName || lastName.trim() === '') {
      errors.lastName = 'Incorrect last name';
    }

    if (!email || email.trim() === '' || !isValidEmail(email)) {
      errors.email = 'Invalid email format';
    }

    const studentIdError = getSisUserCustomFieldError(studentId, 'Student ID');
    if (studentIdError) {
      errors.studentId = studentIdError;
    }

    const degreeCourseError = getSisUserCustomFieldError(
      degreeCourse,
      'Degree Course',
    );
    if (degreeCourseError) {
      errors.degreeCourse = degreeCourseError;
    }

    if (Object.keys(errors).length > 0) {
      return errors;
    }

    return null;
  };

  handleSave = async ({
    staff,
    admin,
    studentId,
    degreeCourse,
    ...updateUser
  }) => {
    try {
      const userRoles = ['general'];
      if (admin) {
        userRoles.push('admin');
      }
      if (staff) {
        userRoles.push('staff');
      }
      const custom = mergeUserCustomFields(this.state.custom, {
        studentId,
        degreeCourse,
      });
      const updated = await this.props.updateUser(
        this.props.match.params.userId,
        {
          ...updateUser,
          userRoles,
          custom,
        },
      );

      if (updated) {
        const savedCustom = updated.custom || custom;
        this.setState({
          textFields: {
            firstName: updateUser.firstName,
            lastName: updateUser.lastName,
            email: updateUser.email,
            externalId: updateUser.externalId,
            studentId: savedCustom.studentId || '',
            degreeCourse: savedCustom.degreeCourse || '',
            sso: getSSO({ user: updateUser, institution: this.props as any }),
            userRoles,
          },
          custom: savedCustom,
          avatar: 'avatar' in updated ? updated.avatar : null,
        });
      }
    } catch (_) {
      // Revert changes
      const { textFields, avatar } = this.state;
      this.setState({
        avatar,
        textFields,
      });
    }
  };

  getRoleString = (roleId) => {
    if (roleId === 'student') {
      return 'student';
    }
    if (roleId === 'educator') {
      return 'educator';
    }
    if (!roleId || !this.props.roles) {
      return 'educator';
    }
    const role = this.props.roles.find((r) => (r.id || r._id) === roleId);
    if (role) {
      const roleName = (role.title || role.name || '').toLowerCase();
      // If the selected role is "student", return "student", otherwise return "educator"
      return roleName === 'student' ? 'student' : 'educator';
    }
    return 'educator';
  };

  onRoleChange = async (roleId, spaceExternalId) => {
    const space =
      this.state.spaces.find((s) => s.externalId === spaceExternalId) || {};
    const spaceId = (space as any).id || spaceExternalId;

    // Get role string for backward compatibility (student/educator)
    const roleString = this.getRoleString(roleId);

    const { webAppFtForRbac202505 } = this.props.flags || {};
    await this.props.updateRole({
      externalUserId: this.state.textFields.externalId,
      externalSpaceId: spaceExternalId,
      role: roleString,
      roleId: webAppFtForRbac202505 ? roleId : undefined,
    });

    const roleName = this.getRoleName(roleId);

    if (roleId) {
      analyticsTrackEvent(ADMIN_ROLE_ASSIGNED_TO_USER, {
        space_id: spaceId,
        role_id: roleId,
        role_name: roleName,
        source: 'admin_user',
        assigned_user_id: this.state.userId,
      });
    }

    this.setState({
      spaces: this.state.spaces.map((s) => {
        if (s.externalId === spaceExternalId) {
          return {
            ...s,
            role: roleString,
            roleId,
            roleName,
          };
        }
        return s;
      }),
    });
  };

  handleRemoveUser = () => {
    this.removeUserModal(true);
  };

  removeUserModal = (open) => {
    this.setState({ removeUser: open });
  };

  removeSpaceFromState = (ids) => {
    this.setState({
      spaces: this.state.spaces.filter((u) => !ids.includes(u.externalId)),
      selected: this.state.selected.filter((id) => !ids.includes(id)),
    });
  };

  removeUser = async () => {
    this.setState({ isRemoving: true });
    const { selected } = this.state;
    const spacesByExternalId = this.state.spaces.reduce((acc, space) => {
      acc[space.externalId] = space;
      return acc;
    }, {});
    const dataToDelete = selected.map((externalSpaceId) => ({
      externalSpaceId,
      externalUserId: this.state.textFields.externalId,
    }));
    const chunks = chunk(dataToDelete, CHUNK_SIZE);
    for (const roleChunk of chunks) {
      try {
        await this.props.deleteRoles(roleChunk);
        roleChunk.forEach(({ externalSpaceId }) => {
          const space = spacesByExternalId[externalSpaceId];
          if (space && space.roleId) {
            analyticsTrackEvent(ADMIN_USER_REMOVED_FROM_SPACE, {
              space_id: space.id || externalSpaceId,
              source: 'admin_user',
            });
          }
        });
        this.removeSpaceFromState(selected);
      } catch (e) {
        // pick out only initial error message as all errors are joined together
        const message =
          `${e}`.split('\n')[0].split('"')[1] || 'Failed to remove space(s).';
        this.props.showToast({
          message,
        });
      }
    }
    this.setState({ isRemoving: false });
    this.removeUserModal(false);
  };

  renderCustomFields = () => {
    const { custom } = this.state;
    const isBooleanField = (value) => typeof value === 'boolean';

    if (JSON.stringify(custom) !== '{}') {
      const fields = Object.keys(custom)
        .filter(
          (field) =>
            !(SIS_USER_CUSTOM_FIELD_KEYS as readonly string[]).includes(field),
        )
        .reduce((visitedFields, currentField) => {
          visitedFields.push({
            label: startCase(currentField),
            value: custom[currentField],
            placeholder: '',
            type: isBooleanField(custom[currentField]) ? 'switch' : 'name',
            id: currentField,
            styles: {
              width: '150px',
            },
            disabled: true,
          });

          return visitedFields;
        }, []);

      if (!fields.length) {
        return null;
      }

      return <BasicInfoForm fields={fields} showAvatar={false} />;
    }
    return null;
  };

  render() {
    const { classes, history, sso: institutionSSO, roles } = this.props;
    const {
      firstName,
      lastName,
      email,
      externalId,
      studentId,
      degreeCourse,
      sso,
      userRoles,
    } = this.state.textFields;

    if (this.state.loading) {
      return (
        <div className={classes.loaderContainer}>
          <CircularProgress />
        </div>
      );
    }

    return (
      <div className={classes.container}>
        <div className={classes.basicInfoForm}>
          <BasicInfoForm
            overlayActions
            fields={[
              {
                label: 'First Name',
                value: firstName,
                placeholder: 'John',
                type: 'name',
                id: 'firstName',
                styles: {
                  minWidth: '100px',
                },
              },
              {
                label: 'Last Name',
                value: lastName,
                placeholder: 'Doe',
                type: 'name',
                id: 'lastName',
                styles: {
                  minWidth: '100px',
                },
              },
              {
                label: 'Email',
                value: email,
                placeholder: 'Doe',
                type: 'email',
                id: 'email',
                check: true,
                styles: {
                  width: '240px',
                },
              },
              {
                label: 'External ID',
                value: externalId,
                placeholder: '',
                type: 'name',
                id: 'externalId',
                styles: {
                  width: '280px',
                  maxWidth: '280px',
                },
              },
              {
                type: 'break',
                id: 'breakBeforeSisFields',
              },
              {
                label: 'Student ID',
                value: studentId,
                placeholder: '',
                type: 'name',
                id: 'studentId',
                showCountOnEdit: true,
                maxLength: SIS_USER_CUSTOM_FIELD_MAX_LENGTH,
                dynamicWidth: true,
                dynamicWidthMin: 140,
                dynamicWidthMax: 320,
                validateOnChange: (value) =>
                  getSisUserCustomFieldError(value, 'Student ID'),
              },
              {
                label: 'Degree Course',
                value: degreeCourse,
                placeholder: '',
                type: 'name',
                id: 'degreeCourse',
                showCountOnEdit: true,
                maxLength: SIS_USER_CUSTOM_FIELD_MAX_LENGTH,
                dynamicWidth: true,
                dynamicWidthMin: 140,
                dynamicWidthMax: 320,
                validateOnChange: (value) =>
                  getSisUserCustomFieldError(value, 'Degree Course'),
              },
              {
                type: 'spacer',
                id: 'spacerBeforeUserToggles',
              },
              {
                label: 'SSO Enabled',
                value: sso,
                type: 'switch',
                id: 'sso',
                // if institution wide sso flag is off, disable switch
                disabled: !institutionSSO,
              },
              {
                label: 'Admin',
                value: userRoles.includes('admin'),
                type: 'switch',
                id: 'admin',
              },
              {
                label: 'Staff',
                value: userRoles.includes('staff'),
                type: 'switch',
                id: 'staff',
              },
            ]}
            avatarName={`${firstName} ${lastName}`}
            avatarUrl={this.state.avatar}
            preSaveCheck={this.preSaveCheck}
            onSave={this.handleSave}
          />
          {this.renderCustomFields()}
        </div>
        <div className={classes.table}>
          <Paper elevation={5}>
            <UserTable
              title={BtnTitle('ADD SPACES', history, {
                name: `${firstName} ${lastName}`,
                externalId,
                internalId: this.state.userId,
              })}
              data={this.state.spaces}
              columns={['name', 'id', 'externalId', 'role', 'custom']}
              onRoleChange={this.onRoleChange}
              onSelect={this.handleSelect}
              roles={
                (this.props.flags || {}).webAppFtForRbac202505
                  ? roles || []
                  : LEGACY_ROLES
              }
              menuItems={
                <MenuItem
                  className={classes.menuItem}
                  onClick={this.handleRemoveUser}
                >
                  Remove from space
                </MenuItem>
              }
            />
          </Paper>
        </div>
        <Dialog
          id="remove-user-dialog"
          open={this.state.removeUser}
          width="600"
          height="190"
          onClose={() => this.removeUserModal(false)}
        >
          <DialogTitle closeIcon={false}>Remove user from space?</DialogTitle>
          <DialogContent>
            The user will not be able to access this space anymore.
          </DialogContent>
          <DialogActions
            primary={{
              label: 'Remove!',
              onClick: () => this.removeUser(),
              loading: this.state.isRemoving,
            }}
            secondary={{
              label: 'Cancel',
              onClick: () => this.removeUserModal(false),
            }}
          />
        </Dialog>
      </div>
    );
  }
}

export default withStyles(styles, { withTheme: true })(withLDConsumer()(User));
